/**
 * RLQ — Retail Lobby Queue shared types.
 *
 * Per modules digest §RLQ: service types map to required permissions so
 * officers can be gated out of claims they shouldn't handle.
 */

/** Module target a kiosk check-in should route into on claim. */
export type ServiceTypeTargetModule =
  | 'RAO'   // new-account
  | 'RAM'   // account maintenance
  | 'RCM'   // CIF update
  | 'RIM'   // IRA
  | 'RCD'   // CD
  | 'RSM'   // SDB
  | 'RSC'   // signing
  | 'RCC';  // compliance (walk-in complaint, death-notice bring-in, etc.)

/** Permission key required on `ao_UserRoles.ProductPermissions`. */
export type RequiredPermission =
  | 'NewAccount'
  | 'AccountMaintenance'
  | 'CifMaintenance'
  | 'IRA'
  | 'CD'
  | 'SDB'
  | 'Signing'
  | 'Compliance'
  | 'Any';

export interface ServiceTypeOption {
  code: string;
  label: string;
  helper?: string;
  requiredPermission: RequiredPermission;
  targetModule: ServiceTypeTargetModule;
}

/**
 * Fallback service-type catalogue used when no `config.systemSettings['lobby.serviceTypes']`
 * or dedicated config route is seeded. Reflects the narrow FSR-facing set called
 * out in `feedback_lobby_service_routing.md`.
 */
export const FALLBACK_SERVICE_TYPES: ReadonlyArray<ServiceTypeOption> = [
  {
    code: 'new-account',
    label: 'Open a new account',
    helper: 'Checking, savings, CD, SDB.',
    requiredPermission: 'NewAccount',
    targetModule: 'RAO',
  },
  {
    code: 'account-maintenance',
    label: 'Service existing account',
    helper: 'Stop pay, signer change, closure.',
    requiredPermission: 'AccountMaintenance',
    targetModule: 'RAM',
  },
  {
    code: 'cif-update',
    label: 'Update my info',
    helper: 'Address, phone, employer.',
    requiredPermission: 'CifMaintenance',
    targetModule: 'RCM',
  },
  {
    code: 'ira-contribution',
    label: 'IRA contribution / distribution',
    helper: 'Requires IRA permission.',
    requiredPermission: 'IRA',
    targetModule: 'RIM',
  },
  {
    code: 'cd-action',
    label: 'CD maturity / waiver',
    helper: 'Requires CD permission.',
    requiredPermission: 'CD',
    targetModule: 'RCD',
  },
  {
    code: 'sdb-service',
    label: 'Safe deposit box',
    helper: 'Access, vacate, past-due.',
    requiredPermission: 'SDB',
    targetModule: 'RSM',
  },
  {
    code: 'sign-docs',
    label: 'Signing a document',
    helper: 'Returning to finish signatures.',
    requiredPermission: 'Signing',
    targetModule: 'RSC',
  },
  {
    code: 'other',
    label: 'Something else',
    helper: 'A banker will triage.',
    requiredPermission: 'Any',
    targetModule: 'RCC',
  },
];

/** Normalized lobby entry shape used by the officer queue. */
export interface LobbyEntryRow {
  sessionId: string;
  customerName: string;
  customerHint?: string;
  serviceTypeCode?: string;
  serviceTypeLabel?: string;
  requiredPermission?: RequiredPermission;
  targetModule?: ServiceTypeTargetModule;
  waitMinutes: number;
  fsrName?: string;
  branch?: string;
  startedAt: string;
  cifId?: string;
  cartId?: string;
}
